import { Alert, Stack } from "@mui/material";
import { useMemo } from "react";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { Fetching } from "components/common/loading/fetching";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import {
  DateRangeFilterBar,
  useDateRangeFilters,
} from "components/manageLabellers/dateRangeFilterBar";
import { CopyWorkerStatsToClipboardButton } from "components/manageLabellers/copyStatsToClipboardButton";
import {
  buildLabellerBaseColumns,
  buildLabellerBaseCsvColumns,
  LABELLER_NAME_FIELD,
  LabellerBaseRow,
} from "components/manageLabellers/labellerOverviewColumns";
import {
  sumNullable,
  TenantLabellerStatsLoader,
  TenantName,
  TenantStatsState,
  useTenantLabellerStats,
} from "./TenantLabellerStatsLoader";

export const mergeLabellerStats = (
  stateByTenant: Record<TenantName, TenantStatsState>,
  activeTenantNames: readonly string[]
): LabellerBaseRow[] => {
  const merged = new Map<string, LabellerBaseRow>();
  for (const tenantName of activeTenantNames) {
    const state = stateByTenant[tenantName];
    if (!state) continue;
    for (const entry of state.response?.labeller_stats ?? []) {
      if (entry.labeller.labeller_type !== "human") continue;
      const existing = merged.get(entry.labeller.id);
      merged.set(
        entry.labeller.id,
        existing
          ? {
              ...existing,
              total_periods: existing.total_periods + entry.total_periods,
              total_time_labelled_seconds:
                existing.total_time_labelled_seconds +
                entry.total_time_labelled_seconds,
              total_time_spent_labelling_seconds: sumNullable(
                existing.total_time_spent_labelling_seconds,
                entry.total_time_spent_labelling_seconds
              ),
              total_submissions:
                existing.total_submissions + entry.total_submissions,
              events_created: existing.events_created + entry.events_created,
            }
          : {
              total_periods: entry.total_periods,
              id: entry.labeller.id,
              labeller: entry.labeller,
              total_time_labelled_seconds: entry.total_time_labelled_seconds,
              total_time_spent_labelling_seconds:
                entry.total_time_spent_labelling_seconds,
              total_submissions: entry.total_submissions,
              events_created: entry.events_created,
              accuracy: null,
            }
      );
    }
  }
  return [...merged.values()];
};

type CrossTenantLabellerOverviewTabProps = Readonly<{
  tenantNames: readonly string[];
}>;

export const CrossTenantLabellerOverviewTab = ({
  tenantNames,
}: CrossTenantLabellerOverviewTabProps) => {
  const { t } = useTenantTranslation();
  const formatNumber1dp = useFormatNumber()(1);
  const dateFilters = useDateRangeFilters();

  const {
    stateByTenant,
    anyLoading,
    anyFetching,
    failedTenantNames,
    onTenantStatsLoaded,
  } = useTenantLabellerStats(tenantNames);

  const rows = useMemo(
    () => mergeLabellerStats(stateByTenant, tenantNames),
    [stateByTenant, tenantNames]
  );

  const columns = useMemo(
    () =>
      buildLabellerBaseColumns(t, formatNumber1dp, { showLabellerType: false }),
    [t, formatNumber1dp]
  );

  const csvColumns = useMemo(
    () => buildLabellerBaseCsvColumns(t, { showLabellerType: false }),
    [t]
  );

  return (
    <Stack spacing={2} sx={{ flex: 1, overflow: "hidden" }}>
      {failedTenantNames.length > 0 ? (
        <Alert severity="warning">
          {t("partialTenantResults", { tenants: failedTenantNames.join(", ") })}
        </Alert>
      ) : null}
      {tenantNames.map((tenantName) => (
        <TenantLabellerStatsLoader
          key={tenantName}
          tenantName={tenantName}
          dateFilters={dateFilters}
          onLoaded={onTenantStatsLoaded}
        />
      ))}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ pt: 1 }}>
        <DateRangeFilterBar filters={dateFilters} />
        <CopyWorkerStatsToClipboardButton rows={rows} columns={csvColumns} />
      </Stack>
      <Fetching fetching={anyFetching} sx={{ flex: 1, overflow: "hidden" }}>
        <HotkeyCompliantDataGrid
          columns={columns}
          rows={rows}
          loading={anyLoading}
          initialState={{
            pinnedColumns: { left: [LABELLER_NAME_FIELD] },
            sorting: {
              sortModel: [{ field: LABELLER_NAME_FIELD, sort: "asc" }],
            },
          }}
          slotProps={{
            loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" },
          }}
          rowHeight={40}
          columnHeaderHeight={44}
          disableRowSelectionOnClick
        />
      </Fetching>
    </Stack>
  );
};
